import './checkout.scss';

import type { HasFiscalPrinter } from '@pos/app/scripts/services/has-fiscal-printer';
import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Loyalty } from '@shared/app/scripts/services/loyalty';
import { Mask } from '@core/app/scripts/services/mask';
import { PosSearch } from '@pos/app/scripts/services/pos-search';
import { PosState } from '@pos/app/scripts/services/pos-state';
import { SalesManager } from '@shared/app/scripts/services/sales-manager';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Utility } from '@core/app/scripts/services/utility';
import {
  FashionCheque,
  FashionChequeResponse,
} from '@pos/app/scripts/services/fashion-cheque';

/*
 * Component responsible for /checkout route.
 */
angular
  .module('posApp')
  .directive(
    'checkout',
    (
      $injector: ng.IInjectStatic,
      $sce: ng.ISCEService,
      broadcaster: Broadcaster,
      displayMessage: DisplayMessage,
      hasFiscalPrinter: HasFiscalPrinter,
      isFiscalReturn,
      isTransactionStableFilter,
      legacyPrint,
      loyalty: Loyalty,
      mask: Mask,
      navigateTo: NavigateTo,
      parser,
      paymentTypes,
      pinProvider,
      pos,
      posInstance,
      posReasons,
      posSearch: PosSearch,
      posState: PosState,
      printReceipt,
      salesManager: SalesManager,
      sentry: Sentry,
      settingsManager: SettingsManager,
      salesCalculations,
      translate: ng.translate.ITranslateService,
      utility: Utility,
      fashionCheque: FashionCheque,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./checkout.html').default,

      link: function ($scope: any, element, attrs) {
        const $translate = translate;

        $scope.isFiscalReturn = isFiscalReturn;
        $scope.$sce = $sce;

        // Hack! To be replaced by custom operator rx operator
        const wrapInSafeApply =
          require('../../../../../global/wrap-in-safe-apply')($scope);

        $scope.pos = pos;
        $scope.posReasons = posReasons;
        $scope.vat = settingsManager.settings.vat;
        $scope.displayDiscountReason =
          settingsManager.settings.displayAndPrintDiscountReason;

        // POS-1815 inject in a normal way once we load data in right order
        const checkoutInit = $injector.get('checkoutInit');

        $scope.withdrawalreason = '';
        $scope.opendrawerreason = '';
        //These are used in the checkout View to enable/disable buttons
        $scope.paymentTypes = paymentTypes;
        $scope.paymentFieldDisplay = '';
        $scope.showEmailReceipt = false;
        $scope.remark = false;
        $scope.extended = null;
        $scope.pinInProgress = false;
        $scope.payPlazaStatus = '';
        $scope.showPiggyPortal = false;

        // check if clients has piggy
        $scope.hasPiggy = false;
        if (settingsManager.settings.piggyApiToken) {
          $scope.hasPiggy = true;
        }

        $scope.lineType = (line) => salesCalculations.lineType(line);

        // set receiptNr on checkout start
        if (pos.transaction) {
          if (
            pos.transaction.payments &&
            pos.transaction.payments.consignment === 0
          ) {
            pos.transaction.receiptNr = salesManager.counters.sales;
          } else {
            pos.transaction.receiptNr = salesManager.counters.consignments;
          }

          pos.transaction.nr =
            pos.config.cashregisterNr +
            '-' +
            posInstance.nr +
            '-' +
            pos.transaction.receiptNr;
        }

        const totalPaid = 0;

        pinProvider.inProgress$.subscribe(
          wrapInSafeApply((status) => {
            $scope.pinInProgress = status;
          }),
        );

        $scope.navigateToView = (view) => {
          //Nagivation away from this page should only occur when a transaction is complete
          if (!$scope.disableButtons()) {
            navigateTo(view);
          }
        };

        $scope.search = (searchInput) => {
          $scope.searchInput = '';

          //User should not scan a new item when receipt is being processed
          if (!$scope.disableButtons()) {
            posSearch.scan(searchInput);
          }
        };

        $scope.onChangeCashier = () => {
          //User should not change cashier when receipt is being processed
          if (!$scope.disableButtons()) {
            pos.onChangeCashier();
          }
        };

        $scope.toggleRemarkField = () => {
          //User should not change cashier when receipt is being processed
          if (!$scope.disableButtons()) {
            $scope.remark = $scope.remark ? false : true;
            broadcaster.send('setFocus', 'remarkarea');
          }
        };

        $scope.togglePiggyProcess = () => {
          $scope.showPiggyPortal = !$scope.showPiggyPortal;

          if ($scope.showPiggyPortal) {
            $scope.openPiggyPortal();
          } else {
            $scope.closePiggyPortal();
          }
        };

        $scope.openPiggyPortal = () => {
          if ($scope.hasPiggy && pos.transaction.customer) {
            const customerEmail = pos.transaction.customer.email;

            mask.showLoading();

            loyalty.createPiggyPortalSession(customerEmail).then(
              (response) => {
                if (response) {
                  $scope.piggyPortalLink = $sce.trustAsResourceUrl(response);
                } else {
                  $scope.closePiggyPortal();
                  displayMessage.message($translate.instant('ServerError'));
                }

                mask.hide();
              },
              () => {
                $scope.closePiggyPortal();
                displayMessage.message($translate.instant('ServerError'));

                mask.hide();
              },
            );
          }
        };

        $scope.closePiggyPortal = () => {
          $scope.piggyPortalLink = '';
          $scope.showPiggyPortal = false;
        };

        $scope.onKeyPress = (val) => {
          pos.key(val, $scope);
          return;
        };

        broadcaster.send('setFocus', 'search');
        broadcaster.send('windowResized');
        $scope.remark = false;
        $scope.payPlazaStatus = '';

        $scope.extended = settingsManager.settings.pos?.extendedReceipt;

        //Reset the processing transaction boolean (will re-enable all the buttons)
        checkoutInit.processingTransaction = false;

        //Show the email field
        $scope.toggleEmail = () => {
          if (settingsManager.settings.email.active) {
            pos.selectedPayment = '';
            if (pos.transaction.customer) {
              pos.transaction.receipt_email = pos.transaction.customer.email;
            }
            $scope.showEmailReceipt = true;
          } else {
            const message = $translate.instant('MissingEmailingModule');
            displayMessage.message({
              message: message,
              type: 'info',
            });
          }
        };

        /*
         * Disable buttons if the PIN is in progress or if a payment is in progress
         */
        $scope.disableButtons = () => {
          return (
            $scope.pinInProgress ||
            checkoutInit.processingTransaction ||
            !isTransactionStableFilter(pos.transaction)
          );
        };

        //Toggles the currently selected payment method
        $scope.toggleFields = (type) => {
          let message,
            currentAmount = 0,
            change = 0;

          // Payment methods rules
          pos.selectedPayment = type;
          $scope.showEmailReceipt = false;
          //Disable remark field
          $scope.remark = false;

          $scope.payPlazaStatus = '';

          // Set amount due only if PIN and electronic since the user has no
          // way to change the amount to zero himself
          if (
            type == 'pin' &&
            paymentTypes.rules[type].coupled === true &&
            pos.transaction.payments.pin !== 0
          ) {
            pos.transaction.payments.pin = 0;
            pos.updateTotals();
          }

          // creditreceipt
          if (type == 'creditreceipt' && pos.transaction.change <= 0) {
            pos.selectedPayment = '';
            pos.creditreceipt = 0;

            message = $translate.instant('NoMoneyToCredit');
            displayMessage.message(message);
            return message;
          }

          // should have article
          if (
            paymentTypes.rules[type].article === true &&
            pos.transaction.receipt.length === 0
          ) {
            message = $translate.instant('ScanAnArticle');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          // TODO add unit test
          if (
            hasFiscalPrinter() &&
            type === 'cash' &&
            pos.transaction.totalDiscount > 0 &&
            pos.transaction.totalDiscount > pos.transaction.totalAmount
          ) {
            message = $translate.instant('CouponsCannotExceedTotal');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          // electronic payment
          if (
            settingsManager.settings.pinProvider === 'payPlaza4' &&
            paymentTypes.rules[type].coupled === true &&
            !settingsManager.settings.pinId
          ) {
            message = $translate.instant('Missing Payplaza Info');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          } else if (
            settingsManager.settings.pinProvider === 'payNL' &&
            paymentTypes.rules[type].coupled === true &&
            (!settingsManager.settings.payNLDeviceId ||
              !settingsManager.settings.payNLStoreId)
          ) {
            message = $translate.instant('Missing PayNl info');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          } else if (
            settingsManager.settings.pinProvider === 'payNL' &&
            paymentTypes.rules[type].coupled === true &&
            !settingsManager.settings.payNLToken
          ) {
            message = $translate.instant('Missing PayNl token');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }
          // should not have article
          if (
            paymentTypes.rules[type].article === false &&
            pos.transaction.receipt.length > 0
          ) {
            message = $translate.instant('ProcessTransactionFirst');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          const requireCustomer =
            type === 'consignment' ||
            paymentTypes.rules[type].customer === true;

          // should have customer
          if (requireCustomer && pos.transaction.customer === null) {
            message = $translate.instant('CustomerRequiredForPaymentType');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          // should not be negative
          if (paymentTypes.rules[type].allow_neg === false) {
            if (
              utility.round2(
                -pos.transaction.change - pos.transaction.difference,
              ) < 0
            ) {
              message = $translate.instant('NegativeAmountNotAllowed');
              displayMessage.message(message);
              pos.selectedPayment = '';
              return message;
            }
          }

          $scope.paymentFieldDisplay = paymentTypes.rules[type].display;
          currentAmount = pos.transaction.payments[type]
            ? pos.transaction.payments[type]
            : 0;

          // it doesn't update pos.translation! but it was like this already before migration
          // to salesCalculations
          change = salesCalculations.calcChange(pos.transaction);

          //If there has been rounding, remove it if another payment is added that is not cash
          if (salesCalculations.isCashOnly(pos.transaction) && type != 'cash') {
            currentAmount = currentAmount - pos.transaction.difference;
          }

          // Current amount is used only for display
          if (pos.transaction.change === 0) {
            currentAmount = 0;
          } else {
            currentAmount = currentAmount - pos.transaction.change;
          }

          //If it's a cash payment, start rounding!
          if (
            settingsManager.settings.roundTotalAmount5cent &&
            type == 'cash' &&
            change !== 0
          ) {
            currentAmount = utility.roundTo5Cent(currentAmount);
            pos.transaction.roundPayment = true;
          } else {
            pos.transaction.roundPayment = false;
          }

          // Set the string version with 2 decimal points to the amount entry field
          // for display to the user
          pos.transaction.amountEntry = currentAmount.toFixed(2);

          const selectedPaymentTypes = pos.paymentTypeString();

          if (
            (selectedPaymentTypes.indexOf('consignment') == -1 &&
              selectedPaymentTypes.length > 0 &&
              type == 'consignment') ||
            (selectedPaymentTypes.indexOf('consignment') >= 0 &&
              type != 'consignment')
          ) {
            // Enforce that this paymenttype cannot be combined with other types.
            // i.e. (consignment was clicked AND not selected yet AND another method WAS selected ) OR
            //      (something else was clicked AND consignment was already selected)
            message = $translate.instant(
              'ConsignmentCannotBeCombinedWithOtherPaymentTypes',
            );
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          if (
            type == 'consignment' &&
            salesCalculations.containsStoreCredit(pos.transaction)
          ) {
            message = $translate.instant(
              'ConsignmentCannotBeUsedToPayForStoreCredit',
            );
            displayMessage.message(message);
            pos.selectedPayment = '';
            return message;
          }

          if (type == 'consignment') {
            if (pos.transaction.payments.consignment === 0) {
              // Set consignment to the total Amount Due + any discount given
              pos.transaction.payments.consignment =
                pos.transaction.totalAmount -
                pos.transaction.overallReceiptDiscount -
                pos.transaction.totalDiscountCoupon;
            } else {
              pos.transaction.payments.consignment = 0;
            }
            pos.updateTotals();
            pos.selectedPayment = '';
          }

          if (
            type == 'withdrawel' &&
            paymentTypes.rules['withdrawel'].reason === true
          ) {
            broadcaster.send('setFocus', [
              'checkoutAmountInputWithReason',
              true,
            ]);
          } else {
            broadcaster.send('setFocus', ['checkoutAmountInput', true]);
          }

          if (type == 'pin' && paymentTypes.rules.pin.coupled === true) {
            if (
              utility.round2(
                -pos.transaction.change - pos.transaction.difference,
              ) > 100000
            ) {
              message = $translate.instant('GreaterThanMaximumAmount');
              displayMessage.message(message);
              return message;
            }

            // Set the PIN amount on the transaction
            if (!angular.isNumber(pos.transaction.change)) {
              sentry.captureException(
                new Error(
                  'pos.transaction.change is not a number, resetting it to 0',
                ),
              );
              pos.transaction.change = 0;
            }

            if (!angular.isNumber(pos.transaction.difference)) {
              sentry.captureException(
                new Error(
                  'pos.transaction.difference is not a number, resetting it to 0',
                ),
              );
              pos.transaction.difference = 0;
            }

            const amount = pos.transaction.getPendingPayment();

            pos.updateTotals(pos.transaction);

            // Generate Merch Reference to connect the POS transaction to payPlaza
            pos.transaction.merchRef = utility.generateRandomAlphaNumeric(10);

            pos.finalizeTransaction(pos.transaction).then((transaction) => {
              pinProvider.startTransaction(transaction, amount).then(
                (printOut) => {
                  pos.transaction.setPinPayment(amount);

                  if (printOut) {
                    pos.transaction.pinInfo += printOut;
                  } else {
                    sentry.captureException(
                      new Error(
                        'POS-1942 we have success PIN transaction *without* pin receipt. It most likely an error.',
                      ),
                    );
                  }

                  $scope
                    .redeemFashionCheque()
                    .then((fashionChequeResponse) => {
                      if (
                        fashionChequeResponse.length > 0 &&
                        !fashionChequeResponse.every(
                          (response) => response.status === 'ok',
                        )
                      ) {
                        $scope.rollbackFashionCheque(fashionChequeResponse);
                        return Promise.reject(
                          new Error('[FASHION_CHEQUE_ERROR]'),
                        );
                      }
                    })
                    .then(() => {
                      pos.sendReceipt();
                    })
                    .then(() => {
                      $scope.payPlazaStatus = 'accepted';
                      printReceipt();
                    });
                },
                (errorPrintOut) => {
                  function resetTransaction(pinError) {
                    $scope.payPlazaStatus = 'rejected';

                    if (pinError) {
                      pos.transaction.pinError += pinError;
                    }
                    pos.transaction.payments.pin = 0.0;

                    // remove cashback coupon
                    pos.transaction.coupon = pos.transaction.coupon.filter(
                      (coupon) => coupon.type !== 'C',
                    );

                    //Update all totals
                    pos.updateTotals(pos.transaction);
                  }

                  if (errorPrintOut !== '') {
                    // transaction rejected
                    resetTransaction(errorPrintOut);
                  } else if (
                    settingsManager.settings.pinProvider === 'payPlaza4'
                  ) {
                    // PayPlaza specific; payNl service doesn't suppport checkServer yet
                    // if no errorPrintOut, something went wrong
                    pinProvider.checkServer(pos.transaction).then(
                      (result) => {
                        result.pickedUpTransactionPromise.then(
                          (printOut) => {
                            pos.transaction.setPinPayment(
                              // it makes no sense for us to caculate it, but PayPlaza want's us to
                              // restart transaction and provide no information about what amount it
                              // was started with
                              pos.transaction.getPendingPayment(),
                            );

                            pos.transaction.pinInfo += printOut;

                            $scope
                              .redeemFashionCheque()
                              .then((fashionChequeResponse) => {
                                if (
                                  fashionChequeResponse.length > 0 &&
                                  !fashionChequeResponse.every(
                                    (response) => response.status === 'ok',
                                  )
                                ) {
                                  $scope.rollbackFashionCheque(
                                    fashionChequeResponse,
                                  );
                                  return Promise.reject(
                                    new Error('[FASHION_CHEQUE_ERROR]'),
                                  );
                                }
                              })
                              .then(() => {
                                pos.sendReceipt();
                              })
                              .then(() => {
                                $scope.payPlazaStatus = 'accepted';
                                printReceipt();
                              });
                          },
                          (pinError) => {
                            resetTransaction(pinError);
                          },
                        );
                      },
                      (err) => {
                        resetTransaction(err);
                      },
                    );
                  } else {
                    resetTransaction(errorPrintOut);
                  }
                },
              );
            });
          }

          return true;
        };

        $scope.showAmountDialog = () => {
          const result =
            [
              'cash',
              'creditcard',
              'storecredit',
              'consignment',
              'creditreceipt',
            ].includes(pos.selectedPayment) ||
            (pos.selectedPayment == 'pin' &&
              !paymentTypes.rules['pin'].coupled);
          return result;
        };

        $scope.showReasonDialog = () => {
          return (
            pos.selectedPayment == 'totaldiscount' ||
            pos.selectedPayment == 'totalpercentdiscount'
          );
        };

        // give Total discount
        $scope.getTotalDiscount = (type) => {
          pos.selectedPayment = type;

          broadcaster.send('setFocus', [
            'checkoutAmountInputDiscountReason',
            true,
          ]);
        };

        //When Print Button is pressed
        // TODO improve coverage by tests
        $scope.checkout = () => {
          // verify outstanding amount - for returns
          if (pos.transaction.change < 0 && pos.transaction.totalAmount < 0) {
            var message = $translate.instant('return_too_much_message');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return;
          }

          if (
            hasFiscalPrinter() &&
            !(
              pos.transaction.change === 0 ||
              pos.transaction.receipt.length === 0 ||
              pos.transaction.payments.cash !== 0
            )
          ) {
            message = $translate.instant('Select payment');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return;
          }

          // check for return with no payment method
          if (
            // return
            pos.transaction.totalAmount < 0 &&
            // without any payments
            !salesCalculations.hasAnyPayment(pos.transaction) &&
            // and with pending payment different then 0
            pos.transaction.getPendingPayment() !== 0
          ) {
            var message = $translate.instant('choose_return_payment');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return;
          }

          // verify outstanding amount
          if (pos.transaction.change < 0) {
            var message = $translate.instant('Insufficient Funds');
            displayMessage.message(message);
            pos.selectedPayment = '';
            return;
          }

          displayMessage.showLoading();
          checkoutInit.processingTransaction = true;

          pos
            .finalizeTransaction(pos.transaction)
            .then((transaction) => {
              const returnTransaction = posState.returnTransaction;

              // Handle any fashion cheque redemptions if present
              return $scope
                .redeemFashionCheque()
                .then((fashionChequeResponse) => {
                  if (
                    fashionChequeResponse.length > 0 &&
                    !fashionChequeResponse.every(
                      (response) => response.status === 'ok',
                    )
                  ) {
                    return $scope
                      .rollbackFashionCheque(fashionChequeResponse)
                      .then(() => {
                        // Explicitly reject the promise to stop the transaction
                        // rollback successful redemptions
                        return Promise.reject(
                          new Error('[FASHION_CHEQUE_ERROR]'),
                        );
                      });
                  }
                  return transaction;
                });
            })
            .then((transaction) => {
              return pos.sendReceipt().then((transactionId) => {
                transaction._id = transactionId;
                return transaction;
              });
            })
            .then((transaction) => {
              const returnTransaction = posState.returnTransaction;
              return printReceipt(transaction, returnTransaction).catch(
                (error) => {
                  displayMessage.message({
                    message: $translate.instant('PrintingFailedMessage', {
                      message: error?.message,
                    }),
                    time: 4000,
                    type: 'alert',
                  });
                },
              );
            })
            .catch((error) => {
              if (error.message === '[FASHION_CHEQUE_ERROR]') {
                // Error message already displayed above
                return Promise.reject(error); // Re-throw to ensure transaction stops
              } else {
                displayMessage.message($translate.instant('SavingFailed'));
              }
            })
            .finally(() => {
              mask.hide();
              checkoutInit.processingTransaction = false;
            });
        };

        //When Email Button is pressed
        // TODO improve coverage by tests
        $scope.emailReceipt = (email) => {
          let message;

          // verify outstanding amount
          if (pos.transaction.change < 0) {
            message = $translate.instant('Insufficient Funds');
            displayMessage.message(message);
            pos.selectedPayment = '';
            $scope.showEmailReceipt = false;
            return;
          }
          if (
            !(
              pos.transaction.change === 0 ||
              pos.transaction.receipt.length === 0 ||
              pos.transaction.payments.cash !== 0
            )
          ) {
            message = $translate.instant('Select payment');
            displayMessage.message(message);
            pos.selectedPayment = '';
            $scope.showEmailReceipt = false;
            return;
          }

          //Attempt to block the user from doing anything else to the transaction after printing
          checkoutInit.processingTransaction = true;

          pos.finalizeTransaction(pos.transaction).then((transaction) => {
            printReceipt(pos.transaction).then(angular.noop, () => {
              displayMessage.message($translate.instant('PrintingFailed'));
            });

            // Save transaction
            pos
              .sendReceipt()
              .then((savedReceiptId) => {
                legacyPrint
                  .emailReceipt(savedReceiptId, email)
                  .then(angular.noop, (errorMessage) => {
                    displayMessage.message(
                      $translate.instant('MailingFailed') +
                        '<br>' +
                        errorMessage,
                    );
                  });
              })
              .then(
                () => {
                  mask.hide();
                  checkoutInit.processingTransaction = false;
                },
                () => {
                  displayMessage.message($translate.instant('SavingFailed'));
                },
              );

            pos.selectedPayment = '';
            $scope.showEmailReceipt = false;
          });
        };

        const printFailed = () => {
          displayMessage.message($translate.instant('PrintingNotPossible'));
        };

        function resetFields() {
          //Reset fields
          pos.transaction.amountEntry = '0';
          pos.selectedPayment = '';
        }

        //Processes the amount once the OK button is clicked
        $scope.processAmount = () => {
          //Validate Entry Form

          const selectedPayment = pos.selectedPayment;

          let message;

          if ($scope.amountEntryForm?.$valid) {
            pos.transaction.amountEntry = String(
              pos.transaction.amountEntry,
            ).replace(',', '.');
            pos.transaction.payments[selectedPayment] = parser.evaluate(
              pos.transaction.amountEntry,
            );
          } else {
            message = $translate.instant('IncorrectEntry');
            displayMessage.message(message);
            return;
          }

          // Don't allow values above 8 digits
          if (pos.transaction.payments[selectedPayment] > 99999999) {
            message = $translate.instant('Maximum Input Exceeded');
            displayMessage.message(message);
            pos.transaction.payments[selectedPayment] = 0;
            return;
          }

          // allow negative values
          if (
            paymentTypes.rules[selectedPayment].allow_neg === false &&
            pos.transaction.payments[selectedPayment] < 0
          ) {
            message = $translate.instant('Negative Not Allowed');
            displayMessage.message(message);
            pos.transaction.payments[selectedPayment] = 0;
            return;
          }

          // allow positive values
          if (
            paymentTypes.rules[selectedPayment].allow_pos === false &&
            pos.transaction.payments[selectedPayment] > 0
          ) {
            message = $translate.instant('Positive Not Allowed');
            displayMessage.message(message);
            pos.transaction.payments[selectedPayment] = 0;
            return;
          }

          // should give a reason
          if (
            selectedPayment === 'withdrawel' &&
            paymentTypes.rules['withdrawel'].reason === true &&
            pos.transaction.reason === ''
          ) {
            message = $translate.instant('Select Reason');
            displayMessage.message(message);
            return;
          }
          pos.transaction.roundPayment = false;

          //Broadcast to mainController to update all totals
          pos.updateTotals();

          //Reset fields
          resetFields();
        };

        // process Total Discount
        $scope.procTotalDiscount = () => {
          // validate form
          // We used to check to see if the amount was non zero but now we found that there are usecases for 0 as well.
          // Discounts can also be given in other forms than monetary, users want to register them as discounts in the POS
          // nontheless. So by allowing 0, we make sure the discountcode is added to the receiptlines for reporting purposes
          if (!pos.transaction.amountEntry) {
            pos.transaction.amountEntry = '0';
          }
          let amount = pos.transaction.amountEntry.replace(',', '.');
          amount = parser.evaluate(amount);

          if ($scope.amountEntryForm?.$valid) {
            const discount = pos.selectedPayment;
            if (discount == 'totaldiscount') {
              // The discount cannot be greater than the total of the transaction or less than 0
              if (amount > salesCalculations.total(pos.transaction)) {
                displayMessage.message(
                  $translate.instant('Maximum Input Exceeded'),
                );
                return;
              } else if (amount < 0) {
                displayMessage.message(
                  $translate.instant('Negative Not Allowed'),
                );
                return;
              } else {
                pos.transaction.overallReceiptDiscount = utility.round2(amount);
              }
            } else if (discount == 'totalpercentdiscount') {
              // Percentage must be between 0 and 100
              if (amount > 100) {
                displayMessage.message(
                  $translate.instant('Maximum Input Exceeded'),
                );
                return;
              } else if (amount < 0) {
                displayMessage.message(
                  $translate.instant('Negative Not Allowed'),
                );
                return;
              } else {
                if (angular.isDefined(pos.transaction.discountableAmount)) {
                  pos.transaction.overallReceiptDiscount = utility.round2(
                    (amount / 100) * pos.transaction.discountableAmount,
                  );
                } else {
                  pos.transaction.overallReceiptDiscount = 0;
                }
              }
            }
            if (pos.transaction.couponDiscount) {
              pos.transaction.totalDiscount =
                pos.transaction.overallReceiptDiscount +
                pos.transaction.couponDiscount;
            } else {
              pos.transaction.totalDiscount =
                pos.transaction.overallReceiptDiscount;
            }
          } else {
            displayMessage.message($translate.instant('IncorrectEntry'));
            return;
          }

          //Broadcast to mainController to update all totals
          pos.updateTotals();

          //Reset fields
          resetFields();
        };

        $scope.showTotalDiscount = () => {
          return (
            pos.transaction?.receipt?.length &&
            pos.config.acls?.pos_total_discount
          );
        };

        $scope.showTotalPercentageDiscount = () => {
          return (
            pos.transaction?.receipt?.length &&
            pos.config.acls?.pos_total_discount_percentage
          );
        };

        $scope.isFashionChequeCoupon = function (line) {
          return line.couponNr ? fashionCheque.validateCode(line.couponNr) : '';
        };

        $scope.getMaskedCouponNr = function (line) {
          return line.couponNr ? line.couponNr.replace(/\d{4}$/, '****') : '';
        };

        /*
         * Redeem Fashion Cheque
         */
        $scope.redeemFashionCheque = () => {
          try {
            mask.showLoading();

            const redemptionPromises = pos.transaction.receipt
              .filter(
                (receiptLine) =>
                  receiptLine.type === 'U' &&
                  fashionCheque.validateCode(receiptLine.couponNr),
              )
              .map((receiptLine) => {
                const euroCentsAmount = Math.round(receiptLine.nettPrice * 100);

                return fashionCheque
                  .redeemVoucher(receiptLine.couponNr, euroCentsAmount)
                  .then((response: FashionChequeResponse) => {
                    if (response.status === 'ok') {
                      const index = pos.transaction.receipt.findIndex(
                        (element) => element.couponNr === receiptLine.couponNr,
                      );

                      if (index !== -1) {
                        pos.transaction.receipt[index].couponBalance = Number(
                          (response.data.card.amount.value / 100).toFixed(2),
                        );
                      }

                      return response;
                    } else {
                      displayMessage.message(
                        response.data?.message ||
                          $translate.instant('RedeemFashionChequeError'),
                      );
                      mask.hide();
                      return response;
                    }
                  })
                  .catch((error) => {
                    displayMessage.message(
                      error.data?.message ||
                        $translate.instant('RedeemFashionChequeError'),
                    );
                    mask.hide();
                    return error;
                  });
              });
            mask.hide();
            return Promise.all(redemptionPromises);
          } catch (e) {
            return Promise.resolve([]);
          }
        };

        $scope.rollbackFashionCheque = (redemptionResponses) => {
          try {
            mask.showLoading();

            const rollbackPromises = redemptionResponses
              .filter((response) => response.status === 'ok')
              .map((response) => {
                return fashionCheque
                  .rollbackVoucher(
                    response.data.card.number,
                    response.data.requestId,
                  )
                  .then((rollbackResponse) => {
                    if (rollbackResponse.status !== 'ok') {
                      displayMessage.message(
                        rollbackResponse.data?.message ||
                          $translate.instant('RollbackFashionChequeError'),
                      );
                    }
                    return rollbackResponse;
                  })
                  .catch((error) => {
                    displayMessage.message(
                      error.data?.message ||
                        $translate.instant('RollbackFashionChequeError'),
                    );
                    return error;
                  });
              });

            return Promise.all(rollbackPromises).then((results) => {
              mask.hide();
              return results;
            });
          } catch (e) {
            mask.hide();
            return Promise.resolve([]);
          }
        };
      },
    }),
  );
